import Phaser from 'phaser';
import { TILE_SIZE, MAP_WIDTH, MAP_HEIGHT, PLAYER_SPEED, Direction } from '../data/world-config';
import { PLANTS } from '../data/plants';
import { MONSTER_WAVES } from '../data/monsters';
import { DayNightSystem } from '../systems/DayNightSystem';
import { FarmSystem } from '../systems/FarmSystem';
import { PlantGrowthSystem } from '../systems/PlantGrowthSystem';
import { WorldGenerator } from '../systems/WorldGenerator';
import { NightMonsterSystem } from '../systems/NightMonsterSystem';
import { CombatSystem } from '../systems/CombatSystem';
import { StorySystem } from '../systems/StorySystem';
import { AIRivalSystem } from '../systems/AIRivalSystem';
import { FarmExpansionSystem } from '../systems/FarmExpansionSystem';
import { PlantSentinelSystem } from '../systems/PlantSentinelSystem';
import { MilestoneSystem } from '../systems/MilestoneSystem';
import { Player } from '../entities/Player';
import { NetworkManager } from '../network/NetworkManager';
import type { UIScene } from './UIScene';

export class GameScene extends Phaser.Scene {
  player!: Player;

  dayNightSystem!: DayNightSystem;
  farmSystem!: FarmSystem;
  plantGrowthSystem!: PlantGrowthSystem;
  worldGenerator!: WorldGenerator;
  nightMonsterSystem!: NightMonsterSystem;
  combatSystem!: CombatSystem;
  storySystem!: StorySystem;
  aiRivalSystem!: AIRivalSystem;
  farmExpansionSystem!: FarmExpansionSystem;
  plantSentinelSystem!: PlantSentinelSystem;
  milestoneSystem!: MilestoneSystem;
  networkManager!: NetworkManager;

  terrainLayer!: Phaser.Tilemaps.TilemapLayer;
  objectLayer!: Phaser.GameObjects.Group;
  plantLayer!: Phaser.GameObjects.Group;
  otherPlayers!: Map<string, Phaser.GameObjects.Sprite>;

  isMultiplayer: boolean = false;
  isStoryMode: boolean = false;
  private roomId: string = 'default';

  // Track coins earned per night for dawn report
  private coinsAtNightStart: number = 0;

  // Direct DOM key tracking — bypasses Phaser's keyboard plugin to avoid stuck keys
  private keysDown: Set<string> = new Set();
  private onKeyDown!: (e: KeyboardEvent) => void;
  private onKeyUp!: (e: KeyboardEvent) => void;

  constructor() {
    super({ key: 'GameScene' });
  }

  init(data: { newGame?: boolean; multiplayer?: boolean; storyMode?: boolean; roomId?: string }): void {
    this.isMultiplayer = data.multiplayer ?? false;
    this.isStoryMode = data.storyMode ?? false;
    this.roomId = data.roomId ?? 'default';
  }

  create(): void {
    // Generate the world tilemap
    this.worldGenerator = new WorldGenerator(this);
    const { map, terrainLayer } = this.worldGenerator.generate();
    this.terrainLayer = terrainLayer;

    // Object and plant groups
    this.objectLayer = this.add.group();
    this.plantLayer = this.add.group();
    this.otherPlayers = new Map();

    // Create player at center of their farm
    const startX = (MAP_WIDTH / 2) * TILE_SIZE;
    const startY = (MAP_HEIGHT / 2) * TILE_SIZE;
    this.player = new Player(this, startX, startY);

    // Integer zoom only to avoid subpixel scan lines
    // Desktop/laptop: 2x so 16px tiles render at 32px (visible)
    // Mobile: 1x since screens are physically smaller but high-DPI
    const screenW = this.sys.game.canvas.width;
    const screenH = this.sys.game.canvas.height;
    const isMobile = Math.min(screenW, screenH) < 600;
    const cameraZoom = isMobile ? 1 : 2;

    this.cameras.main.startFollow(this.player.sprite, true, 0.1, 0.1);
    this.cameras.main.setZoom(cameraZoom);
    this.cameras.main.setBounds(0, 0, MAP_WIDTH * TILE_SIZE, MAP_HEIGHT * TILE_SIZE);

    // Handle viewport resize (Scale.RESIZE mode)
    this.scale.on('resize', (gameSize: Phaser.Structs.Size) => {
      this.cameras.main.setSize(gameSize.width, gameSize.height);
    });

    // Set world bounds for physics
    this.physics.world.setBounds(0, 0, MAP_WIDTH * TILE_SIZE, MAP_HEIGHT * TILE_SIZE);

    // Initialize core game systems
    this.dayNightSystem = new DayNightSystem(this);
    this.farmSystem = new FarmSystem(this, startX, startY);
    this.plantGrowthSystem = new PlantGrowthSystem(this);

    // Initialize combat systems
    this.nightMonsterSystem = new NightMonsterSystem(this);
    this.combatSystem = new CombatSystem(this);

    // Plant sentinel system (auto-defending plants at night)
    this.plantSentinelSystem = new PlantSentinelSystem(this);

    // Milestone system (daily objectives)
    this.milestoneSystem = new MilestoneSystem(this);

    // Farm expansion
    this.farmExpansionSystem = new FarmExpansionSystem(this, startX, startY);

    // AI Rival (always in single player)
    if (!this.isMultiplayer) {
      this.aiRivalSystem = new AIRivalSystem(this);
    }

    // Story system (fires dialogue events)
    if (this.isStoryMode) {
      this.storySystem = new StorySystem(this);
    }

    // Direct DOM key tracking — completely bypasses Phaser keyboard plugin
    const gameKeys = new Set([
      'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
      'KeyW', 'KeyA', 'KeyS', 'KeyD', 'KeyE', 'KeyI',
      'KeyC', 'KeyB', 'KeyJ', 'KeyK', 'KeyL', 'KeyF',
      'ShiftLeft', 'ShiftRight',
      'Space',
    ]);

    this.onKeyDown = (e: KeyboardEvent) => {
      if (gameKeys.has(e.code)) {
        e.preventDefault();
        const wasDown = this.keysDown.has(e.code);
        this.keysDown.add(e.code);
        if (!wasDown) {
          if (e.code === 'KeyE') this.handleInteract();
          if (e.code === 'KeyI') this.toggleInventory();
          if (e.code === 'KeyC') this.handleCombine();
          if (e.code === 'KeyB') this.handleShop();
          if (e.code === 'KeyF') this.handleToggleSentinel();
          // Space is handled by CombatSystem for sword
        }
      }
    };

    this.onKeyUp = (e: KeyboardEvent) => {
      if (gameKeys.has(e.code)) {
        e.preventDefault();
        this.keysDown.delete(e.code);
      }
    };

    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);

    // Clear all keys when tab loses focus or window is hidden
    const clearKeys = () => {
      this.keysDown.clear();
      this.player.move(0, 0);
    };
    this.game.events.on('blur', clearKeys);
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) clearKeys();
    });

    // Mouse click for placing/interacting
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      this.handleClick(pointer);
    });

    // Launch UI scene on top
    this.scene.launch('UIScene', { gameScene: this });

    // Set up multiplayer if needed
    if (this.isMultiplayer) {
      this.networkManager = new NetworkManager(this);
      this.networkManager.connect(this.roomId);

      // Sync farm actions to server
      this.events.on('farm-planted', (data: { plantId: string; tileX: number; tileY: number }) => {
        this.networkManager.sendPlant(data.plantId, data.tileX, data.tileY);
      });
      this.events.on('farm-watered', (data: { tileX: number; tileY: number }) => {
        this.networkManager.sendWater(data.tileX, data.tileY);
      });
      this.events.on('farm-harvested', (data: { tileX: number; tileY: number }) => {
        this.networkManager.sendHarvest(data.tileX, data.tileY);
      });
    }

    // Start story after a brief delay
    if (this.isStoryMode && this.storySystem) {
      this.storySystem.startStory();
    }

    // Listen for harvest events (for story tracking)
    this.events.on('notification', (msg: string) => {
      if (msg.startsWith('Harvested') || msg.includes('harvest:')) {
        this.events.emit('plant-harvested');
      }
    });

    // Track coins at night start for dawn report
    this.events.on('time-update', (data: { isNight: boolean }) => {
      if (data.isNight && this.coinsAtNightStart === 0) {
        this.coinsAtNightStart = this.combatSystem.playerCoins;
      }
    });

    // Dawn report triggered after night ends
    this.events.on('night-summary', (data: { monstersKilled: number; nightsSurvived: number }) => {
      this.time.delayedCall(1000, () => {
        this.showDawnReport(data);
      });
    });
  }

  shutdown(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    this.combatSystem?.shutdown();
    this.plantSentinelSystem?.shutdown();
    this.networkManager?.disconnect();

    // Clean up other players
    for (const [id] of this.otherPlayers) {
      this.removeOtherPlayer(id);
    }
  }

  update(time: number, delta: number): void {
    // Player movement
    this.handleMovement(delta);

    // Update core systems
    this.dayNightSystem.update(time, delta);
    this.plantGrowthSystem.update(time, delta);

    // Combat system (energy regen, projectiles, combo timer)
    this.combatSystem.update(time, delta);

    // Night monsters
    this.nightMonsterSystem.update(time, delta);

    // Plant sentinel system (auto-attack during night)
    this.plantSentinelSystem.update(time, delta);

    // AI Rival
    if (this.aiRivalSystem) {
      this.aiRivalSystem.update(time, delta);
    }

    // Multiplayer sync
    if (this.isMultiplayer && this.networkManager) {
      this.networkManager.update(time, delta);
    }
  }

  private handleMovement(_delta: number): void {
    const speed = PLAYER_SPEED;
    let vx = 0;
    let vy = 0;

    // Keyboard input via direct DOM tracking (no Phaser isDown)
    const left = this.keysDown.has('ArrowLeft') || this.keysDown.has('KeyA');
    const right = this.keysDown.has('ArrowRight') || this.keysDown.has('KeyD');
    const up = this.keysDown.has('ArrowUp') || this.keysDown.has('KeyW');
    const down = this.keysDown.has('ArrowDown') || this.keysDown.has('KeyS');

    if (left) vx = -speed;
    else if (right) vx = speed;

    if (up) vy = -speed;
    else if (down) vy = speed;

    // Touch joystick input (adds to keyboard, so both work)
    const uiScene = this.scene.get('UIScene') as UIScene;
    if (uiScene?.touchControls?.isActive()) {
      const joy = uiScene.touchControls.getDirection();
      if (joy.x !== 0 || joy.y !== 0) {
        vx = joy.x * speed;
        vy = joy.y * speed;
      }
    }

    // Normalize diagonal movement
    if (vx !== 0 && vy !== 0) {
      const factor = Math.SQRT1_2;
      vx *= factor;
      vy *= factor;
    }

    // Feed directional input to combat system for special move detection
    if (vx !== 0 || vy !== 0) {
      this.combatSystem.feedDirectionInput(this.player.direction);

      // Dodge roll: shift + direction
      if (this.keysDown.has('ShiftLeft') || this.keysDown.has('ShiftRight')) {
        if (this.combatSystem.tryDodge(vx, vy)) {
          return; // Dodge takes over movement
        }
      }
    }

    this.player.move(vx, vy);
  }

  /** Public interact method for touch controls to call. */
  handleInteractPublic(): void {
    this.handleInteract();
  }

  /** Public sword swing for touch controls. */
  handleAttackPublic(): void {
    this.combatSystem?.swingSwordPublic();
  }

  /** Public sentinel toggle for touch controls. */
  handleSentinelPublic(): void {
    this.handleToggleSentinel();
  }

  private handleInteract(): void {
    // First check if near rival for battle
    if (this.aiRivalSystem?.interactWithRival()) {
      return;
    }

    const playerTileX = Math.floor(this.player.sprite.x / TILE_SIZE);
    const playerTileY = Math.floor(this.player.sprite.y / TILE_SIZE);

    // Get the tile the player is facing
    const facingOffset = this.player.getFacingTileOffset();
    const targetTileX = playerTileX + facingOffset.x;
    const targetTileY = playerTileY + facingOffset.y;

    // Try farm expansion first
    if (this.farmExpansionSystem.tryExpand(targetTileX, targetTileY)) {
      return;
    }

    // Try to interact with farm plot
    this.farmSystem.interact(targetTileX, targetTileY, this.player);
  }

  /** Toggle sentinel mode on the facing farm plot (F key). */
  private handleToggleSentinel(): void {
    const playerTileX = Math.floor(this.player.sprite.x / TILE_SIZE);
    const playerTileY = Math.floor(this.player.sprite.y / TILE_SIZE);
    const facingOffset = this.player.getFacingTileOffset();
    const targetTileX = playerTileX + facingOffset.x;
    const targetTileY = playerTileY + facingOffset.y;

    const key = `${targetTileX},${targetTileY}`;
    const plots = this.farmSystem.getPlots();
    const plot = plots.get(key);

    if (!plot || !plot.plantId) {
      this.events.emit('notification', 'No plant here to set as sentinel.');
      return;
    }

    if (plot.growthStage < 2) {
      this.events.emit('notification', 'Plant must be mature (stage 2+) to be a sentinel.');
      return;
    }

    plot.isDefender = !plot.isDefender;

    if (plot.isDefender) {
      const plantName = PLANTS[plot.plantId]?.name ?? plot.plantId;
      this.events.emit('notification', `${plantName} set as SENTINEL! Will defend at night.`);
      this.milestoneSystem?.onSentinelPlaced();

      // Visual: green pulse on the plant
      const plantSprites = this.farmSystem.getPlantSprites();
      const sprite = plantSprites.get(key);
      if (sprite) {
        sprite.setTint(0x88ff88);
        this.tweens.add({
          targets: sprite,
          scaleX: 1.3,
          scaleY: 1.3,
          duration: 200,
          yoyo: true,
          ease: 'Back.easeOut',
          onComplete: () => sprite.clearTint(),
        });
      }
    } else {
      this.events.emit('notification', 'Sentinel mode disabled for this plant.');
    }
  }

  private handleClick(pointer: Phaser.Input.Pointer): void {
    const worldPoint = this.cameras.main.getWorldPoint(pointer.x, pointer.y);
    const tileX = Math.floor(worldPoint.x / TILE_SIZE);
    const tileY = Math.floor(worldPoint.y / TILE_SIZE);

    // Try farm expansion first
    if (this.farmExpansionSystem.tryExpand(tileX, tileY)) {
      return;
    }

    // Try to interact with that tile
    this.farmSystem.interact(tileX, tileY, this.player);
  }

  private toggleInventory(): void {
    this.events.emit('toggle-inventory');
  }

  private handleCombine(): void {
    this.farmSystem.tryCombine(this.player);
  }

  private handleShop(): void {
    this.events.emit('open-shop');
  }

  /** Show the Dawn Report screen after surviving a night. */
  private showDawnReport(data: { monstersKilled: number; nightsSurvived: number }): void {
    const uiScene = this.scene.get('UIScene') as UIScene;
    if (!uiScene || !('showDawnReport' in uiScene)) return;

    const coinsEarned = this.combatSystem.playerCoins - this.coinsAtNightStart;
    this.coinsAtNightStart = 0;

    // Claim milestone bonus
    const milestoneStatus = this.milestoneSystem.getMilestoneStatus();
    const bonus = this.milestoneSystem.claimDawnBonus();

    // Generate forecast for next night
    const nextDay = this.dayNightSystem.getDayCount();
    const { forecast, tip } = this.generateNightForecast(nextDay);

    uiScene.showDawnReport({
      nightNumber: data.nightsSurvived,
      monstersKilled: data.monstersKilled,
      sentinelsLost: this.plantSentinelSystem.sentinelsLost,
      coinsEarned: Math.max(0, coinsEarned),
      milestones: milestoneStatus.milestones,
      milestonesCompleted: milestoneStatus.completedCount,
      milestonesTotal: milestoneStatus.total,
      bonusCoins: bonus?.coins ?? 0,
      bonusFertilizer: bonus?.fertilizer ?? 0,
      bonusSeed: bonus?.rareSeed,
      forecast,
      forecastTip: tip,
    });
  }

  private generateNightForecast(dayCount: number): { forecast: string; tip: string } {
    // Find which monster tier unlocks next
    let currentTier = MONSTER_WAVES[0];
    let nextTier: typeof currentTier | null = null;
    for (const wave of MONSTER_WAVES) {
      if (dayCount >= wave.minDay) currentTier = wave;
      else if (!nextTier) nextTier = wave;
    }

    const monsterNames = currentTier.monsters.map(id => {
      const monster = { piranha_plant: 'Piranha Plants', thorn_crawler: 'Thorn Crawlers', fungal_brute: 'Fungal Brutes', wilt_wraith: 'Wilt Wraiths', root_titan: 'Root Titans' }[id] ?? id;
      return monster;
    });

    let forecast = `Expect: ${monsterNames.join(', ')}`;
    if (nextTier && dayCount + 1 >= nextTier.minDay) {
      forecast += ` (NEW threats incoming!)`;
    }

    // Tips based on upcoming enemies
    let tip = 'Plant sentinels near your farm entrance for best coverage.';
    if (currentTier.monsters.includes('fungal_brute')) {
      tip = 'Fire Pepper sentinels deal burst AOE — great against slow Brutes.';
    }
    if (currentTier.monsters.includes('wilt_wraith')) {
      tip = 'Shadow Vine sentinels can snare fast Wraiths. Position them on flanks.';
    }
    if (currentTier.monsters.includes('root_titan')) {
      tip = 'Root Titans are boss-tier. Stack multiple sentinels and keep your sword ready!';
    }

    return { forecast, tip };
  }

  addOtherPlayer(id: string, x: number, y: number): void {
    if (this.otherPlayers.has(id)) return; // Don't duplicate
    const sprite = this.add.sprite(x, y, 'player', 0);
    sprite.setTint(0xaaaaff);
    // Add a name label above the sprite
    const label = this.add.text(x, y - 14, id.slice(0, 8), {
      fontFamily: 'monospace', fontSize: '7px', color: '#aaaaff',
      stroke: '#000000', strokeThickness: 2,
    });
    label.setOrigin(0.5, 1);
    sprite.setData('label', label);
    this.otherPlayers.set(id, sprite);
  }

  updateOtherPlayer(id: string, x: number, y: number, direction: Direction): void {
    const sprite = this.otherPlayers.get(id);
    if (sprite) {
      // Smooth interpolation: lerp over ~100ms instead of instant teleport
      const dx = Math.abs(sprite.x - x);
      const dy = Math.abs(sprite.y - y);
      if (dx > 200 || dy > 200) {
        // Large jump (respawn/teleport) — snap immediately
        sprite.setPosition(x, y);
      } else if (dx > 1 || dy > 1) {
        this.tweens.add({
          targets: sprite,
          x, y,
          duration: 100,
          ease: 'Linear',
        });
      }

      const dirs = ['down', 'left', 'right', 'up'];
      const anim = `player-walk-${dirs[direction]}`;
      if (sprite.anims?.exists?.(anim)) {
        sprite.play(anim, true);
      }

      // Update label position
      const label = sprite.getData('label') as Phaser.GameObjects.Text | undefined;
      if (label) {
        label.setPosition(x, y - 14);
      }
    }
  }

  removeOtherPlayer(id: string): void {
    const sprite = this.otherPlayers.get(id);
    if (sprite) {
      const label = sprite.getData('label') as Phaser.GameObjects.Text | undefined;
      label?.destroy();
      sprite.destroy();
      this.otherPlayers.delete(id);
    }
  }
}
